import { useState, useEffect } from "react";
import { Button, IconButton, TextField } from "@mui/material";
import MilestoneContent from "./milestone-content";
import { axiosInstance } from "./../../utility/apiInstance";

import toast from "react-hot-toast";
import { MilestoneItem, SelectOption, TabType, typeMap } from "./type";

export default function AirMilestoneManage({ groundStaffOptions }: { groundStaffOptions: SelectOption[] }) {
  const [activeTab, setActiveTab] = useState<TabType>("first-mile");

  // -------- separate states --------

  const [firstNewMilestones, setFirstNewMilestones] = useState<MilestoneItem[]>([]);

  const [lastNewMilestones, setLastNewMilestones] = useState<MilestoneItem[]>([]);
  const [getMiddlemilestones, setGetMiddleMilestones] = useState<MilestoneItem[]>([]);

  const [loading, setLoading] = useState(false);
  const [editingMiddleId, setEditingMiddleId] = useState<string | null>(null);

  const inputBackground = "#F5F7FA";


  const fetchMilestones = async (tab: TabType) => {
    if (tab === "middle-mile") return;

    try {
      const type = typeMap[tab];
      const { data } = await axiosInstance.get(`/api/v1/ocean-manual-milestone?type=${type}`);
      const mileStoneData = data.data || [];

      const mapped = mileStoneData.map((m: any) => ({
        id: m._id,
        name: m.name,
        description: m.description,
        type: m.type,
        isFromApi: true,

        groundStaffIds: m.groundStaffIds || [],
        groundStaffOptions: groundStaffOptions,
      }));

      if (tab === "first-mile") {
        setFirstNewMilestones(mapped);
        if (mileStoneData.length === 0) {
          setFirstNewMilestones([{ id: String(Date.now()), name: "", description: "", isNew: true, groundStaffOptions: groundStaffOptions, }]);
        }
      } else {
        setLastNewMilestones(mapped);
        if (mileStoneData.length === 0) {
          setLastNewMilestones([{ id: String(Date.now()), name: "", description: "", isNew: true, groundStaffOptions: groundStaffOptions, }]);
        }
      }
    } catch (err) {
    }
  };


  useEffect(() => {
    fetchMilestones(activeTab);
  }, [activeTab]);

  const handleTabChange = (newTab: TabType) => {
    setActiveTab(newTab);
  };

  // -------- Handlers for first & last --------
  const handleMilestoneChange = (
    id: string,
    field: keyof MilestoneItem,
    value: any,
    tab: TabType
  ) => {
    const updater = (m: MilestoneItem) =>
      m.id === id ? { ...m, [field]: value } : m;

    if (tab === "first-mile") {
      setFirstNewMilestones(prev => prev.map(updater));
    } else {
      setLastNewMilestones(prev => prev.map(updater));
    }
  };


  const handleSaveMilestones = async (tab: TabType) => {
    setLoading(true);
    const milestonesToSave =
      tab === "first-mile"
        ? firstNewMilestones.filter((m) => m.name.trim() || m.description.trim())
        : lastNewMilestones.filter((m) => m.name.trim() || m.description.trim());

    if (milestonesToSave.length === 0) {
      toast.error("No milestones to save.");
      setLoading(false);
      return;
    }

    const payloadType = typeMap[tab];
    try {
      const payload = milestonesToSave.map(m => ({
        id: m.id,
        name: m.name,
        description: m.description,
        type: payloadType!,
        groundStaffIds: m?.groundStaffIds || [],
      }));

      await axiosInstance.post("/api/v1/ocean-manual-milestone", payload);
      toast.success("Milestones saved successfully!");
      await fetchMilestones(tab);
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async (id: string) => {
    try {
      const { data } = await axiosInstance.delete(`/api/v1/ocean-manual-milestone/${id}`);
      toast.success(data?.message || "Milestone deleted successfully!");
      await fetchMilestones(activeTab);
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message);
    }
  };


  // -------------------------- Middle Mile Code --------------------------
  const getMiddleMilestone = async () => {
    try {
      const res = await axiosInstance.get("/api/v1/ocean-custom-milestone");

      setGetMiddleMilestones(res?.data?.data || []);
    } catch (error) {

    }
  };

  useEffect(() => {
    getMiddleMilestone();
  }, []);

  const handleCustomMilestoneChange = (index: number, value: string) => {
    const updated = [...getMiddlemilestones];
    updated[index].customMilestone = value;
    setGetMiddleMilestones(updated);
  };

  const handleMiddleEditClick = (id: string) => {
    setEditingMiddleId(id);
  };

  const handleSaveOrUpdate = async () => {
    setLoading(true);

    if (!editingMiddleId) {
      const milestonesToSave = getMiddlemilestones.filter(
        (item) =>
          !item._id &&
          item.customMilestone &&
          item.customMilestone.trim() !== ""
      );

      if (milestonesToSave.length === 0) {
        toast.error("No milestones to save.");
        setLoading(false);
        return;
      }

      try {
        for (const item of milestonesToSave) {
          const { data } = await axiosInstance.post("/api/v1/ocean-custom-milestone", {
            milestone: item.milestone,
            customMilestone: item.customMilestone,
            type: "middle",
          });
        }
        toast.success("Milestone(s) saved successfully!");

        await getMiddleMilestone();
      } catch (error: any) {
        const message = error.response?.data?.message || error.message || "Unknown error";
        toast.error(message);

      } finally {
        setLoading(false);
      }
    } else {
      setLoading(true);

      try {

        let toUpdate = getMiddlemilestones.filter((ele) => ele._id != undefined)
        await Promise.all(
          toUpdate.map((item) =>
            axiosInstance.put(`/api/v1/ocean-custom-milestone/${item._id}`, {
              milestone: item.milestone,
              customMilestone: item.customMilestone,
              type: "middle",
            })
          )
        );

        toast.success("Middle mile milestones updated successfully!");
        setEditingMiddleId(null);
        getMiddleMilestone();
      } catch (error: any) {
        const message = error.response?.data?.message || error.message || "Unknown error";
        toast.error(message);
      } finally {
        setLoading(false);
      }
    }
  };


  const handleAddMilestone = () => {
    const newItem = {
      id: String(Date.now()),
      name: "",
      description: "",
      isNew: true,
      groundStaffOptions: groundStaffOptions
    };

    if (activeTab === "first-mile") {
      setFirstNewMilestones(prev => [newItem,...prev ]);
    }

    if (activeTab === "last-mile") {
      setLastNewMilestones(prev => [newItem,...prev ]);
    }
  };


  // -------------------------- Render --------------------------
  return (
    <>
      <MilestoneContent
        activeTab={activeTab}
        milestones={
          activeTab === "first-mile" ? firstNewMilestones : activeTab === "last-mile" ? lastNewMilestones : []
        }
        handleTabChange={handleTabChange}
        addMilestone={handleAddMilestone}
        removeMilestone={handleDelete}
        handleMilestoneChange={(id, field, value) =>
          handleMilestoneChange(id, field, value, activeTab)
        }
        parentType="ocean"
      />

      {(activeTab === "first-mile" || activeTab === "last-mile") && (
        <div className="mt-6 flex flex-col items-start">
          <Button
            className="btnTheme btnPadding"
            onClick={() =>
              activeTab === "first-mile" ? handleSaveMilestones("first-mile") : handleSaveMilestones("last-mile")
            }
            disabled={loading}

          >

            {loading
              ? "loading ..."
              : "save"}
          </Button>
        </div>
      )}


      {/* Middle Mile Section */}
      {activeTab === "middle-mile" && (
        <>
          <div className="sticky top-0 z-10 bg-gray-50 bgHead border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-center py-1">

              {/* Milestone Name – 35% */}
              <div
                className="h-[40px] px-3 flex items-center text-sm font-semibold text-gray-600 tracking-wide"
                style={{ flex: "0 0 35%" }}
              >
                Milestone Name
              </div>

              {/* Custom Milestone Name – remaining */}
              <div className="h-[40px] px-3 flex items-center text-sm font-semibold text-gray-600 tracking-wide flex-1">
                Custom Milestone Name
              </div>

            </div>
          </div>

          {getMiddlemilestones.map((item: any, index) => (
            <div
              key={item._id || item.milestone}
              className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center mt-2 w-full"
              onClick={() => item._id && handleMiddleEditClick(item._id)}
              style={{ cursor: item._id ? "pointer" : "default" }}
            >
              {/* Milestone Name (35%) */}
              <TextField
                className="input-milestone"
                variant="outlined"
                size="small"
                placeholder="Milestone Name"
                value={item.milestone || ""}
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: {
                    backgroundColor: inputBackground,
                    border: "none",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                }}
                sx={{
                  flex: "0 0 35%",   // 👈 fixes width to 35%
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                  },
                }}
              />

              {/* Custom Name (Remaining width) */}
              <TextField
                className="input-milestone"
                variant="outlined"
                size="small"
                placeholder="Custom Name"
                value={item.customMilestone || ""}
                onChange={(e) => handleCustomMilestoneChange(index, e.target.value)}
                fullWidth
                InputProps={{
                  sx: {
                    backgroundColor: inputBackground,
                    border: "none",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                }}
                sx={{
                  flex: "1",
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                  },
                }}
              />
            </div>
          ))}



          <div className="mt-6 flex flex-col items-start">
            <Button
              className="btnTheme btnPadding"
              disabled={loading}
              onClick={handleSaveOrUpdate}

            >
              {loading
                ? editingMiddleId
                  ? "Updating..."
                  : "Saving..."
                : editingMiddleId
                  ? "Update Milestone"
                  : "Save Milestone"}
            </Button>

          </div>
        </>
      )}
    </>
  );
}
